import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import StockService from '../Api_Module/Api_Services/StockService';
import { useCallback, useRef, useState, useEffect } from 'react';
import Analytics from '../services/analytics';

interface StockSearchProps {
    className?: string;
    placeholder?: string;
    autoFocus?: boolean;
    onSelect?: (symbol: string) => void;
}

export const StockSearch = ({
    className = '',
    placeholder = 'Search for stocks (e.g. AAPL, MSFT, GOOGL...)',
    autoFocus = false,
    onSelect
}: StockSearchProps) => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<Array<{ symbol: string; name: string }>>([]);
    const [isFocused, setIsFocused] = useState(false);
    const currentRequestId = useRef(0);
    const searchRef = useRef<HTMLDivElement>(null);

    const handleNavigate = (symbol: string) => {
        Analytics.event('stock_search', { symbol: symbol.toUpperCase() });
        setSearchTerm('');
        setResults([]);
        if (onSelect) {
            onSelect(symbol);
        } else {
            navigate(`/${symbol}/summary`);
        }
    };

    const debouncedSearch = useCallback(
        debounce(async (value: string, requestId: number) => {
            if (value.trim().length < 1) {
                if (currentRequestId.current === requestId) {
                    setResults([]);
                    setIsLoading(false);
                }
                return;
            }

            setIsLoading(true);
            try {
                const res = await StockService.getStockSymbolsSearch(value);
                if (currentRequestId.current === requestId) {
                    const items: any[] = (res?.symbols || res?.data || res || []) as any[];
                    const mapped = (items || []).map((it: any) => ({
                        symbol: it.symbol || it.ticker || it.code || '',
                        name: it.name || it.companyName || it.label || '',
                    })).filter((r: any) => r.symbol || r.name);
                    setResults(mapped);
                }
            } catch (error) {
                console.error('Error searching stocks:', error);
                if (currentRequestId.current === requestId) {
                    setResults([]);
                }
            } finally {
                if (currentRequestId.current === requestId) {
                    setIsLoading(false);
                }
            }
        }, 250),
        []
    );

    const handleSearch = useCallback((value: string) => {
        const newRequestId = Date.now();
        currentRequestId.current = newRequestId;
        setSearchTerm(value);
        debouncedSearch(value, newRequestId);
    }, [debouncedSearch]);

    const clearSearch = () => {
        setSearchTerm('');
        setResults([]);
        currentRequestId.current = 0;
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const showResults = isFocused && (isLoading || results.length > 0 || (searchTerm && searchTerm.length >= 1));

    return (
        <div className={`relative w-full ${className}`} ref={searchRef}>
            <div className="relative">
                <input
                    type="text"
                    placeholder={placeholder}
                    className="w-full py-2 px-3 pl-10 pr-8 rounded-md bg-theme-secondary border border-theme-tertiary text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    autoFocus={autoFocus}
                />
                <Search className="absolute left-3 top-2.5 text-theme-muted" size={18} />
                {searchTerm && (
                    <button
                        onClick={clearSearch}
                        className="absolute right-2 top-2.5 text-theme-muted hover:text-theme-primary transition-colors"
                        aria-label="Clear search"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>

            {showResults && (
                <div className="absolute left-0 right-0 mt-1 bg-theme-secondary rounded-lg shadow-lg border border-theme-tertiary overflow-hidden z-50">
                    {isLoading ? (
                        <div className="p-4 text-center text-theme-muted">Searching...</div>
                    ) : results.length > 0 ? (
                        <ul className="max-h-60 overflow-y-auto">
                            {results.map((stock) => (
                                <li
                                    key={stock.symbol}
                                    role="button"
                                    tabIndex={0}
                                    className="px-4 py-2 hover:bg-theme-tertiary cursor-pointer flex justify-between items-center"
                                    onClick={() => handleNavigate(stock.symbol)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleNavigate(stock.symbol);
                                        }
                                    }}
                                >
                                    <span className="font-medium text-theme-primary">
                                        {stock.symbol || stock.name}
                                    </span>
                                    {(stock.symbol && stock.name) && (
                                        <span className="text-sm text-theme-muted truncate ml-2">- {stock.name}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : searchTerm && searchTerm.length >= 1 ? (
                        <div className="p-4 text-center text-theme-muted">No results found for "{searchTerm}"</div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default StockSearch;
